// A live project in the workbench: data from the builder API, the AI's
// work streamed from /events (fetch with the bearer: EventSource cannot
// send an Authorization header).
import { useEffect, useRef, useState } from "preact/hooks";
import { api, apiText, ApiError, authHeaders } from "../lib/api";
import { lang, t } from "../i18n";
import { bearer } from "../lib/auth";
import type { Example, ProjectDocument, SizeReport, InputFile, Me, Project, ReviewItem, ReviewSummary, Revision, SampleResult, Turn, Validation, Version } from "../lib/types";
import { Workbench } from "../workbench/Workbench";
import { idleLive, type GeoFamily, type GeoSuggestion, type LogicModule, type SetupGeo, type Actions, type FieldInfo, type Live, type SetupState, type Vocabulary, type WorkbenchData } from "../workbench/model";

let vocab: Promise<Vocabulary | null> | null = null;
import { Centered } from "../App";

async function optional<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 422)) return null;
    throw e;
  }
}

async function load(id: string): Promise<Omit<WorkbenchData, "live">> {
  const [me, proj, inputs, review, turns, cfg] = await Promise.all([
    api<Me>("GET", "/me"),
    api<{ project: Project; ai_running?: boolean }>("GET", `/projects/${id}`),
    api<InputFile[]>("GET", `/projects/${id}/inputs`),
    api<{ items: ReviewItem[]; summary: ReviewSummary }>("GET", `/projects/${id}/review`),
    api<Turn[]>("GET", `/projects/${id}/turns`),
    fetch("/api/v1/config").then((r) => r.json() as Promise<{ ai_ready?: boolean; logic_types?: boolean }>),
  ]);
  let files: Revision["files"] = [];
  if (proj.project.head_rev > 0) files = (await api<Revision>("GET", `/projects/${id}/revisions/head`)).files;
  const validation = await optional(api<Validation>("GET", `/projects/${id}/validation`));
  let setup: SetupState | null = null;
  if (!files?.some((f) => f.path === "manifest.yaml")) {
    const protoFiles = (files ?? []).map((f) => f.path).filter((p) => p.startsWith("proto/") && p.endsWith(".proto"));
    setup = { scenario: proj.project.scenario, protoFiles, messages: [], error: "" };
    if (protoFiles.length && setup.scenario === "A") {
      try {
        setup.messages = (await api<{ messages: string[] }>("GET", `/projects/${id}/proto-messages`)).messages;
      } catch (e) {
        setup.error = (e as Error).message;
      }
    }
  }
  const requirements = proj.project.scenario === "B" ? await api<ProjectDocument>("GET", `/projects/${id}/documents/requirements`) : null;
  // logic types (GIOTHUB-31): only with the switch on
  let modules: LogicModule[] = [];
  let logic: WorkbenchData["logic"] = null;
  const logicTypes = !!(me.logic_types || cfg.logic_types);
  if (logicTypes) {
    modules = (await api<{ modules: LogicModule[] }>("GET", "/modules")).modules;
    if (files?.some((f) => f.path === "manifest.yaml")) {
      const man = await apiText(`/projects/${id}/revisions/head/files/manifest.yaml`);
      if (/^protocol:\s*logic\s*$/m.test(man)) {
        const mod = /^\s+module:\s*(\S+)/m.exec(man)?.[1] ?? "";
        const ver = /^logic:[\s\S]*?^\s+version:\s*['"]?([^'"\n]+)/m.exec(man)?.[1] ?? "";
        logic = { module: mod, version: ver };
      }
    }
  }
  // geo meaning tags (GIOTHUB-64): only with the engine feature on
  const geoTags = !!me.geo_tags;
  let geoFamilies: GeoFamily[] = [];
  let geolocation: SetupGeo | null = null;
  if (geoTags) {
    geoFamilies = (await api<{ families: GeoFamily[] }>("GET", "/geo-tags")).families;
    if (files?.some((f) => f.path === "manifest.yaml")) geolocation = manifestGeolocation(await apiText(`/projects/${id}/revisions/head/files/manifest.yaml`));
  }
  const name = me.name || me.email || me.user_id;
  return {
    demo: false,
    me: {
      name, role: me.role, org: me.organisation!.name || me.organisation!.id, region: me.organisation!.region.toUpperCase(),
      storePublisher: me.store_publisher, tokenStorage: me.token_storage,
      initials: name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]!.toUpperCase()).join(""),
    },
    project: proj.project,
    files: files ?? [],
    inputs,
    items: review.items,
    summary: review.summary,
    validation,
    turns,
    aiPaused: !cfg.ai_ready,
    aiUsage: "-",
    setup,
    requirements,
    logicTypes,
    modules,
    logic,
    geoTags,
    geoFamilies,
    paramClass: !!me.param_class,
    geolocation,
  };
}

/** The manifest's geolocation block, read from its YAML text (the builder writes it block style). */
function manifestGeolocation(man: string): SetupGeo | null {
  const m = /^geolocation:\n((?:[ \t]+.*\n?)*)/m.exec(man);
  if (!m) return null;
  const b = m[1];
  const flag = (k: string) => new RegExp(`^\\s+${k}:\\s*true`, "m").test(b);
  const inputsInline = /^\s+inputs:\s*\[([^\]]*)\]/m.exec(b)?.[1];
  const inputsBlock = /^\s+inputs:\s*\n((?:\s+- .*\n?)*)/m.exec(b)?.[1];
  const inputs = (inputsInline !== undefined ? inputsInline.split(",") : (inputsBlock ?? "").split("\n").map((l) => l.replace(/^\s*-\s*/, "")))
    .map((s) => s.trim().replace(/['"]/g, "")).filter(Boolean);
  const uere = /^\s+uere_m:\s*([\d.]+)/m.exec(b)?.[1];
  return { enabled: flag("enabled"), inputs, reported: flag("reported"), uere_m: uere ? Number(uere) : undefined };
}

function b64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** Package path of an uploaded source: .proto under proto/, payload samples under samples/. */
function sourcePath(name: string): string {
  if (name.endsWith(".proto")) return `proto/${name}`;
  if (/\.(bin|hex)$/.test(name) || /\.(expected|meta)\.json$/.test(name)) return `samples/${name}`;
  return name;
}

export function ProjectPage({ id }: { id: string }) {
  const [data, setData] = useState<Omit<WorkbenchData, "live"> | null>(null);
  const [live, setLive] = useState<Live>(idleLive);
  const [err, setErr] = useState("");
  const reloading = useRef<Promise<void> | null>(null);
  const reload = () => {
    if (!reloading.current)
      reloading.current = load(id).then(setData).catch((e) => setErr(e.message)).finally(() => (reloading.current = null));
    return reloading.current;
  };

  useEffect(() => {
    reload();
    // live events; reconnects after a drop
    let stop = false;
    const ctl = new AbortController();
    (async () => {
      while (!stop) {
        try {
          // no token yet (sign-in still restoring): wait instead of a 401
          const tok = await bearer();
          if (!tok) throw new Error("events: not signed in yet");
          const r = await fetch(`/api/v1/projects/${id}/events`, { headers: await authHeaders(), signal: ctl.signal });
          if (!r.ok || !r.body) throw new Error(`events ${r.status}`);
          const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
          let buf = "";
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += value;
            let i;
            while ((i = buf.indexOf("\n\n")) >= 0) {
              const chunk = buf.slice(0, i);
              buf = buf.slice(i + 2);
              const line = chunk.split("\n").find((l) => l.startsWith("data: "));
              if (line) onEvent(JSON.parse(line.slice(6)));
            }
          }
        } catch {
          if (stop) return;
        }
        await new Promise((res) => setTimeout(res, 3000));
      }
    })();
    return () => {
      stop = true;
      ctl.abort();
    };
  }, [id]);

  const onEvent = (e: { type: string; state?: string; text?: string; id?: string; name?: string; summary?: string; error?: string; running?: boolean }) => {
    switch (e.type) {
      case "hello":
        if (e.running) setLive((l) => ({ ...l, running: true }));
        break;
      case "turn":
        if (e.state === "started") setLive({ running: true, text: "", steps: [], error: "" });
        else {
          reload().then(() => setLive({ ...idleLive, error: e.error ?? "" }));
        }
        break;
      case "text":
        setLive((l) => ({ ...l, running: true, text: l.text + (e.text ?? "") }));
        break;
      case "tool":
        setLive((l) => {
          const steps = l.steps.filter((s) => s.id !== e.id);
          const prev = l.steps.findIndex((s) => s.id === e.id);
          const step = { id: e.id ?? "", tool: e.name ?? "", text: e.summary ?? "", state: (e.state ?? "running") as "running" | "done" | "error" };
          if (prev >= 0) steps.splice(prev, 0, step);
          else steps.push(step);
          return { ...l, running: true, steps };
        });
        break;
      case "review":
      case "files":
      case "validation":
      case "documents":
        reload();
        break;
    }
  };

  if (err) return <Centered>{err}</Centered>;
  if (!data) return <Centered>{t("common.loading")}</Centered>;

  const actions: Actions = {
    async decide(item, action, answer) {
      await api("POST", `/projects/${id}/review/${item}`, { action, answer });
      await reload();
    },
    async acceptHigh(keys) {
      await api("POST", `/projects/${id}/review/accept-high`, { keys });
      await reload();
    },
    async validate() {
      await api("POST", `/projects/${id}/validate`);
      await reload();
    },
    async send(text) {
      setLive({ running: true, text: "", steps: [], error: "" });
      try {
        await api("POST", `/projects/${id}/turns`, { text, lang: lang() });
      } catch (e) {
        setLive(idleLive);
        throw e;
      }
      await reload();
    },
    async uploadSources(files) {
      const put: Record<string, { base64: string }> = {};
      for (const f of files) put[sourcePath(f.name)] = { base64: b64(await f.arrayBuffer()) };
      await api("POST", `/projects/${id}/revisions`, { parent: data.project.head_rev, message: `Upload: ${files.map((f) => f.name).join(", ")}`, put });
      await reload();
    },
    async uploadInputs(files) {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name);
        await api("POST", `/projects/${id}/inputs`, fd);
      }
      await reload();
    },
    async setup(form) {
      await api("POST", `/projects/${id}/setup`, form);
      await reload();
    },
    async applyReview() {
      await api("POST", `/projects/${id}/apply-review`);
      await reload();
    },
    async edit(item, proposal) {
      await api("POST", `/projects/${id}/review/${item}`, { action: "edit", edited: proposal });
      await reload();
    },
    readFile(path) {
      return apiText(`/projects/${id}/revisions/head/files/${path}`);
    },
    async saveFile(path, text) {
      await api("POST", `/projects/${id}/revisions`, { parent: data.project.head_rev, message: `Edited ${path}`, put: { [path]: { text } } });
      await reload();
    },
    geoSuggest(field, unit) {
      return api<{ suggestions: GeoSuggestion[] }>("GET", `/geo-tags/suggest?field=${encodeURIComponent(field)}&unit=${encodeURIComponent(unit)}`)
        .then((r) => r.suggestions).catch(() => []);
    },
    fieldInfo(field) {
      return optional(api<FieldInfo>("GET", `/projects/${id}/inspect?field=${encodeURIComponent(field)}`));
    },
    vocabulary() {
      return (vocab ??= optional(api<Vocabulary>("GET", "/vocabulary")));
    },
    versions() {
      return api<Version[]>("GET", `/projects/${id}/versions`);
    },
    async setPublishing(t) {
      await api("PUT", `/projects/${id}/publishing`, t);
      await reload();
    },
    newDeployKey() {
      return api<{ public_key: string; key: string }>("POST", `/projects/${id}/publishing/ssh-key`);
    },
    async createVersion(v) {
      await api("POST", `/projects/${id}/versions`, { version: v });
      await reload();
    },
    async reviewVersion(v) {
      await api("POST", `/projects/${id}/versions/${v}/review`);
    },
    async publishVersion(v, acceptIssues) {
      await api("POST", `/projects/${id}/versions/${v}/publish`, { confirm: true, accept_issues: acceptIssues });
    },
    async refreshVersion(v) {
      await api("POST", `/projects/${id}/versions/${v}/refresh`);
    },
    examples() {
      return api<Example[]>("GET", `/projects/${id}/examples`);
    },
    async putExample(ex) {
      await api("PUT", `/projects/${id}/examples/${encodeURIComponent(ex.name)}`, ex);
      await reload();
    },
    async deleteExample(name) {
      await api("DELETE", `/projects/${id}/examples/${encodeURIComponent(name)}`);
      await reload();
    },
    revisions() {
      return api<Revision[]>("GET", `/projects/${id}/revisions`);
    },
    document(name) {
      return api<ProjectDocument>("GET", `/projects/${id}/documents/${name}`);
    },
    async putDocument(name, content, version) {
      const d = await api<ProjectDocument>("PUT", `/projects/${id}/documents/${name}`, { content, version });
      await reload();
      return d;
    },
    async confirmRequirements(version) {
      await api("POST", `/projects/${id}/documents/requirements/confirm`, { version });
      await reload();
    },
    sizes() {
      return optional(api<SizeReport>("GET", `/projects/${id}/sizes`));
    },
    async firmwareZip() {
      const r = await fetch(`/api/v1/projects/${id}/firmware.zip`, { headers: await authHeaders() });
      if (!r.ok) throw new ApiError(r.status, ((await r.json().catch(() => null)) as { error?: string } | null)?.error ?? r.statusText);
      return r.blob();
    },
    runExamples(preview) {
      return api<SampleResult[]>("POST", `/projects/${id}/samples${preview ? "?preview=1" : ""}`);
    },
  };
  return <Workbench data={{ ...data, live }} actions={actions} />;
}
